import { useRef } from 'react';
import { ActionIcon, Button, Group, Select, Stack } from '@cowbell/ui';
import { useOutputCatalog } from '../../hooks/useMooTasks';
import { OutputSchemaForm } from './OutputSchemaForm';
import type { OutputChannel, OutputKind } from '../../types/moo-tasks.types';

interface OutputChannelsEditorProps {
  readonly outputs: OutputChannel[];
  readonly onChange: (outputs: OutputChannel[]) => void;
  /** Label for the add-channel control. Defaults to "+ Add channel". */
  readonly addChannelLabel?: string;
}

let rowSeq = 0;
const nextRowKey = () => `output-row-${rowSeq++}`;

/** Channel editor shared by the task wizard Outputs step and the chat
 *  async-task confirmation card. */
export function OutputChannelsEditor({
  outputs,
  onChange,
  addChannelLabel = '+ Add channel',
}: OutputChannelsEditorProps) {
  const { data } = useOutputCatalog();
  const catalog = data?.outputs ?? [];

  // OutputChannel rows have no natural id, and index keys would re-associate
  // component state on removal. Every mutation flows through this component:
  // adds append (key generated by the length sync below), removals splice the
  // matching key, edits replace in place — so each row keeps its key for life.
  const rowKeysRef = useRef<string[]>([]);
  const rowKeys = rowKeysRef.current;
  while (rowKeys.length < outputs.length) rowKeys.push(nextRowKey());
  if (rowKeys.length > outputs.length) rowKeys.length = outputs.length;
  const rows = outputs.map((output, i) => ({ output, rowKey: rowKeys[i] }));

  const schemaFor = (type: string) =>
    catalog.find((o) => o.type === type)?.configSchema ?? {
      type: 'object',
      properties: {},
    };

  const update = (
    i: number,
    patch: Partial<{ type: OutputKind; config: Record<string, unknown> }>
  ) => onChange(outputs.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));

  const remove = (i: number) => {
    rowKeys.splice(i, 1);
    onChange(outputs.filter((_, idx) => idx !== i));
  };

  return (
    <Stack gap="md">
      {rows.map(({ output: o, rowKey }, i) => (
        <Stack key={rowKey} gap="xs">
          <Group align="flex-end" gap="xs">
            <Select
              label="Channel"
              style={{ flex: 1 }}
              comboboxProps={{ withinPortal: true, zIndex: 1400 }}
              data={catalog.map((c) => ({
                value: c.type,
                label: c.displayName,
              }))}
              value={o.type}
              onChange={(v) =>
                update(i, { type: (v ?? 'email') as OutputKind, config: {} })
              }
            />
            <ActionIcon
              aria-label="remove channel"
              variant="subtle"
              onClick={() => remove(i)}
            >
              ✕
            </ActionIcon>
          </Group>
          <OutputSchemaForm
            schema={schemaFor(o.type)}
            value={o.config}
            onChange={(config) => update(i, { config })}
          />
        </Stack>
      ))}
      <Button
        variant="subtle"
        onClick={() =>
          onChange([
            ...outputs,
            { type: (catalog[0]?.type ?? 'email') as OutputKind, config: {} },
          ])
        }
      >
        {addChannelLabel}
      </Button>
    </Stack>
  );
}
